/**
 * Voyage Data Composable
 * Handles data fetching for voyage details, summary, and telemetry data
 */

import axios from 'axios';
import VoyageSummarySvc from '@/services/VoyageSummarySvc';
import SummarySvc from '@/services/SummarySvc';
import { fromApiDatetime, apiDatetimeToUnix } from '@/util/apiDatetime';

export function useVoyageData() {
  // Bucket HARUS habis membagi 24 jam biar dilayani cache backend: HOUR×1 buat
  // voyage pendek, DAY×1 buat yang panjang (±titik chart tetep wajar). JANGAN
  // hitung aggregatedLength dinamis dari durasi — bucket kayak HOUR×43 bypass
  // cache & jatuh ke query lama yang berat.
  const getTelemetryAggregationParams = (voyage) => {
    const start = fromApiDatetime(voyage?.mvygStartTime);
    const end = fromApiDatetime(voyage?.mvygEndTime);
    const hours = start.isValid() && end.isValid()
      ? Math.max((end.unix() - start.unix()) / 3600, 0)
      : 0;

    return {
      aggregatedUnit: hours > 7 * 24 ? 'DAY' : 'HOUR',
      aggregatedLength: 1,
    };
  };

  // Satu request per tipe device untuk SELURUH rentang voyage — backend
  // meng-cache per hari, jadi mecah-mecah rentang malah bikin lambat.
  const fetchTelemetryByType = async (voyage, devcType, aggregation = null) => {
    if (!voyage || !voyage.mvygMassId) {
      return null;
    }

    const response = await SummarySvc.getSummary({
      devcType: [devcType],
      devcMassId: voyage.mvygMassId,
      start: apiDatetimeToUnix(voyage.mvygStartTime),
      end: apiDatetimeToUnix(voyage.mvygEndTime),
      // compare nyamain bucket dua voyage
      ...(aggregation || getTelemetryAggregationParams(voyage)),
      showDetails: true,
    });

    return response.data?.data?.[devcType] || response.data?.[devcType] || response.data || null;
  };

  /**
   * Fetch computed voyage summary metrics by ID
   * @param {string} voyageId - Voyage ID
   * @returns {Promise<Object|null>} Summary data or null
   */
  const fetchSummary = async (voyageId) => {
    try {
      const response = await VoyageSummarySvc.fetchById(voyageId);
      return response || null;
    } catch (error) {
      // Error diterusin apa adanya biar pesan asli backend nyampe ke toast.
      if (!axios.isCancel(error)) {
        console.error('fetchSummary error:', error);
      }
      throw error;
    }
  };

  /**
   * Fetch GPS data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @param {Object} [aggregation] - Optional { aggregatedUnit, aggregatedLength } override
   * @returns {Promise<Object|null>} GPS data or null
   */
  const fetchGpsData = async (voyage, aggregation = null) => {
    try {
      return await fetchTelemetryByType(voyage, 'gps', aggregation);
    } catch (error) {
      console.error('fetchGpsData error:', error);
      return null;
    }
  };

  /**
   * Fetch RPM data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @param {Object} [aggregation] - Optional { aggregatedUnit, aggregatedLength } override
   * @returns {Promise<Object|null>} RPM data or null
   */
  const fetchRpmData = async (voyage, aggregation = null) => {
    try {
      return await fetchTelemetryByType(voyage, 'rpm', aggregation);
    } catch (error) {
      console.error('fetchRpmData error:', error);
      return null;
    }
  };

  /**
   * Fetch flowmeter (fuel consumption) data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @param {Object} [aggregation] - Optional { aggregatedUnit, aggregatedLength } override
   * @returns {Promise<Object|null>} Flowmeter data or null
   */
  const fetchFlowmeterData = async (voyage, aggregation = null) => {
    try {
      return await fetchTelemetryByType(voyage, 'flowmeter', aggregation);
    } catch (error) {
      console.error('fetchFlowmeterData error:', error);
      return null;
    }
  };

  /**
   * Load all voyage data (convenience method)
   * @param {Object} context - Context object with voyageId
   * @returns {Promise<Object>} Object with all loaded data
   */
  const loadAllVoyageData = async ({ voyageId }) => {
    const summary = await fetchSummary(voyageId);
    const voyage = summary?.master || null;

    return {
      voyage, summary,
    };
  };

  /**
   * Load telemetry data (GPS, RPM, Flowmeter) for a voyage
   * @param {Object} voyage - Voyage object
   * @param {Object} [aggregation] - Optional { aggregatedUnit, aggregatedLength } override
   * @returns {Promise<Object>} Object with gpsData, rpmData, flowmeterData
   */
  const loadTelemetryData = async (voyage, aggregation = null) => {
    const [gpsData, rpmData, flowmeterData] = await Promise.all([
      fetchGpsData(voyage, aggregation),
      fetchRpmData(voyage, aggregation),
      fetchFlowmeterData(voyage, aggregation),
    ]);

    return {
      gpsData, rpmData, flowmeterData,
    };
  };

  return {
    // Individual fetch methods
    fetchSummary,
    fetchGpsData,
    fetchRpmData,
    fetchFlowmeterData,
    getTelemetryAggregationParams,
    // Convenience methods
    loadAllVoyageData,
    loadTelemetryData,
  };
}

export default useVoyageData;
